import { Box, Divider, Paper, Typography, useTheme, FormControl, MenuItem, Select } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import DonutChart from "../../components/chart/DonutChart";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";

// ── Styling (matches EngagementDashboardGlobalView) ──────────────────────────
const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});
const filterSelectSx = (isDark, accentColor) => ({
  ...cellTextSx(isDark),
  minWidth: 160,
  "& .MuiSelect-select": { py: "4px", px: 0, pb: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  borderBottom: `2px solid ${accentColor}`,
  borderRadius: 0,
  bgcolor: "transparent",
});

const COLORS = ["#22c55e", "#3b82f6", "#f97316", "#a855f7", "#ec4899", "#14b8a6", "#eab308", "#ef4444", "#06b6d4", "#8b5cf6"];

/**
 * AdminGlobalView
 * ─────────────────────────────────────────────────────────────
 * A reusable analytics dashboard for the Admin and Usage modules.
 * Seamlessly usable across Admin pages (User/Company mgmt) and Usage dashboards.
 *
 * @param {Array}   usages          - Raw usage array (for usage contexts)
 * @param {Array}   sectionUsages   - Aggregated by section (for usage contexts)
 * @param {number}  totalPoints     - Sum of all points (for usage contexts)
 * @param {string}  entityLabel     - Label for the entity (e.g. "User", "Member")
 * @param {string}  accentColor     - Primary brand color for charts
 *
 * @param {Array}   overrideDonutData - [{ label, value, color, percent }]
 * @param {Array}   overrideBarData   - [{ label, [barKey]: value }]
 * @param {string}  barKey            - Key for the bar chart value (default "Points")
 * @param {string}  donutTitle        - Custom title for donut section
 * @param {string}  barTitle          - Custom title for bar section
 * @param {string}  unitLabel         - e.g. "Points", "Users", "Licenses"
 * @param {boolean} hideFilters       - Hide the top module/user/company filters
 * @param {boolean} hideTrend         - Hide the daily trend chart (if used)
 * @param {boolean} hideLeaders       - Hide the top 5 list
 */
const AdminGlobalView = ({
  usages = [],
  sectionUsages = [],
  totalPoints = 0,
  entityLabel = "User",
  accentColor = "#16a34a",

  // Generic Overrides
  overrideDonutData = null,
  overrideBarData = null,
  barKey = "Points",
  donutTitle = null,
  barTitle = null,
  unitLabel = "Points",
  hideFilters = false,
  hideTrend = false,
  hideLeaders = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [filterModule, setFilterModule] = useState("All");
  const [filterUser, setFilterUser] = useState("All");
  const [filterCompany, setFilterCompany] = useState("All");

  const moduleOptions = useMemo(() => {
    return [...new Set(usages.map(u => u.section_name).filter(Boolean))].sort();
  }, [usages]);

  const userOptions = useMemo(() => {
    return [...new Set(usages.map(u => u.username).filter(Boolean))].sort();
  }, [usages]);

  const companyOptions = useMemo(() => {
    return [...new Set(usages.map(u => u.company_name).filter(Boolean))].sort();
  }, [usages]);

  const filteredUsages = useMemo(() => {
    return usages.filter(u => {
      if (filterModule !== "All" && u.section_name !== filterModule) return false;
      if (filterUser !== "All" && u.username !== filterUser) return false;
      if (filterCompany !== "All" && u.company_name !== filterCompany) return false;
      return true;
    });
  }, [usages, filterModule, filterUser, filterCompany]);

  const filteredTotalPoints = useMemo(() => {
    if (overrideDonutData) return overrideDonutData.reduce((sum, d) => sum + d.value, 0);
    return filteredUsages.reduce((sum, u) => sum + (Number(u.point) || 0), 0);
  }, [filteredUsages, overrideDonutData]);

  const filteredSectionUsages = useMemo(() => {
    const totals = {};
    filteredUsages.forEach(u => {
      totals[u.section_name] = (totals[u.section_name] || 0) + (Number(u.point) || 0);
    });
    return Object.entries(totals).map(([section_name, point]) => ({ section_name, point }));
  }, [filteredUsages]);

  // ── Donut: Points distribution by module ───────────────────────────────────
  const donutData = useMemo(() => {
    if (overrideDonutData) return overrideDonutData;

    const sorted = [...filteredSectionUsages].sort((a, b) => b.point - a.point);
    const data = sorted.length > 7
      ? [...sorted.slice(0, 7), { section_name: "Other", point: sorted.slice(7).reduce((s, x) => s + x.point, 0) }]
      : sorted;

    return data
      .filter((s) => s.point > 0)
      .map((s, i) => ({
        label: s.section_name,
        value: s.point,
        color: COLORS[i % COLORS.length],
        percent: filteredTotalPoints > 0 ? (s.point / filteredTotalPoints) * 100 : 0,
      }));
  }, [filteredSectionUsages, filteredTotalPoints, overrideDonutData]);

  // ── Bar chart: Top 15 endpoints or custom data ─────────────────────────────
  const barChartData = useMemo(() => {
    if (overrideBarData) return overrideBarData;

    const endpointTotals = {};
    filteredUsages.forEach((u) => {
      const key = u.endpoint_name || "Unknown";
      endpointTotals[key] = (endpointTotals[key] || 0) + (Number(u.point) || 0);
    });
    return Object.entries(endpointTotals)
      .map(([label, pts]) => {
        let name = label;
        if (name.length > 50) name = name.substring(0, 50) + "...";
        return { label: name, [barKey]: pts };
      })
      .filter((d) => d[barKey] > 0)
      .sort((a, b) => b[barKey] - a[barKey])
      .slice(0, 15);
  }, [filteredUsages, overrideBarData, barKey]);

  // ── Top 5 Module Leaders ──────────────────────────────────────────────────
  const topModules = useMemo(() => {
    return [...filteredSectionUsages]
      .sort((a, b) => b.point - a.point)
      .slice(0, 5);
  }, [filteredSectionUsages]);

  return (
    <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>

      {/* ═══════════════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ mb: tok.spacing.pageGap, pb: tok.sectionHeading.titlePb, borderBottom: tok.sectionHeading.borderBottom, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box>
            <Typography sx={{
              fontWeight: tok.sectionHeading.fontWeight,
              color: tok.sectionHeading.color,
              fontFamily: tok.sectionHeading.fontFamily,
              fontSize: tok.sectionHeading.fontSize,
              lineHeight: 1.2,
              textTransform: 'none',
              borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`,
              pl: 1,
              textAlign: 'left'
            }}>
              {t("Portfolio Analytics")}
            </Typography>
        </Box>

        {/* Global Filters */}
        {!hideFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            {/* Company Filter (for UsagePlatforms / All Company) */}
            {entityLabel === "Platform" && companyOptions.length > 0 && (
              <FormControl variant="outlined" size="small">
                <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
                  <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Enterprises")}</MenuItem>
                  {companyOptions.map((name) => (
                    <MenuItem key={name} value={name} sx={cellTextSx(isDark)}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* User Filter (for UsageCompany) */}
            {entityLabel === "Company" && userOptions.length > 0 && (
              <FormControl variant="outlined" size="small">
                <Select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
                  <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Members")}</MenuItem>
                  {userOptions.map((name) => (
                    <MenuItem key={name} value={name} sx={cellTextSx(isDark)}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Module Filter (Always available) */}
            {moduleOptions.length > 0 && (
              <FormControl variant="outlined" size="small">
                <Select value={filterModule} onChange={(e) => setFilterModule(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
                  <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Modules")}</MenuItem>
                  {moduleOptions.map((mod) => (
                    <MenuItem key={mod} value={mod} sx={cellTextSx(isDark)}>{t(mod)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: tok.spacing.pageGap }}>
        {/* ═══════════════════════════════════════════════════════════
            LEFT: Primary Bar Chart
        ═══════════════════════════════════════════════════════════ */}
        <Box sx={{ flex: "1 1 60%", minWidth: 450 }}>
          <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family, fontSize: ORG_PALETTE.font.size.chartTitle }}>
                  {barTitle || t("Top Endpoints by Consumption")}
                </Typography>
            </Box>

            {barChartData.length > 0 ? (
              <HorizontalStackedBarChart
                 data={barChartData}
                 dataKeys={[barKey]}
                 getColor={() => accentColor}
                 showLegend={false}
                 height={Math.max(barChartData.length * 45 + 100, 350)}
                 barSize={20}
                 xAxisLabel={t(barKey)}
                 yAxisLabel={t("Distribution")}
               />
            ) : (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center", opacity: 0.5 }}>
                <Typography sx={cellTextSx(isDark)}>{t("No statistical data available.")}</Typography>
              </Box>
            )}
          </Paper>
        </Box>

      {/* ═══════════════════════════════════════════════════════════
          RIGHT: Donut Distribution
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 30%", minWidth: 350 }}>
        <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
          <Box sx={{ mb: 3, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family, fontSize: ORG_PALETTE.font.size.chartTitle }}>
                {donutTitle || t("Consumption Topology")}
              </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <DonutChart
              data={donutData}
              size={200}
              strokeWidth={22}
              centerContent={
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ ...cellTextSx(isDark), display: "block", color: isDark ? "#94a3b8" : "#64748b", fontSize: '12px', fontWeight: 600 }}>{t("Total")}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: GRAPH_PALETTE.font.weights.heavy, color: tok.text.primary, lineHeight: 1.2, fontFamily: GRAPH_PALETTE.font.family, fontSize: '28px' }}>
                    {filteredTotalPoints.toLocaleString()}
                  </Typography>
                  <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b", fontSize: '12px', fontWeight: 600 }}>{t(unitLabel)}</Typography>
                </Box>
              }
            />
          </Box>

          {/* Legend */}
          <Box sx={{ pr: 1 }}>
            {donutData.map((d, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                      {d.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                    {d.value.toLocaleString()} ({Math.round(d.percent)}%)
                  </Typography>
                </Box>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                </Box>
              </Box>
            ))}
          </Box>

          {!hideLeaders && filteredUsages.length > 0 && (
            <>
              <Divider sx={{ mt: 3, mb: 2.5, opacity: 0.35 }} />
              <Box>
                <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
                  <Typography variant="h6" sx={{
                    fontWeight: GRAPH_PALETTE.font.weights.medium,
                    color: tok.text.chartTitle,
                    textAlign: "left",
                    m: 0,
                    fontFamily: GRAPH_PALETTE.font.family,
                    fontSize: ORG_PALETTE.font.size.chartTitle
                  }}>
                    {t("Top 5 Active Modules")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {topModules.map((mod, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLORS[i % COLORS.length] }} />
                        <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                          {mod.section_name}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.primary }}>
                        {mod.point.toLocaleString()} pts
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  </Box>
);
};

export default AdminGlobalView;
